import { useAuthContext } from '@/auth/useAuthContext';
import { isSafeReturnTo } from '@/customer/lib/return_to';
import { cn } from '@/lib/utils';
import { Loader2, Lock, Mail, PawPrint } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

type Tab = 'magic' | 'password';

export function LoginPage() {
  const [tab, setTab] = useState<Tab>('magic');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuthContext();
  const [params] = useSearchParams();
  // Only honour a root-relative `next` so the login page can never become an
  // open redirect. Anything else lands on the dashboard.
  const rawNext = params.get('next');
  const next = isSafeReturnTo(rawNext) ? rawNext : '/today';

  const sendMagic = async () => {
    setError(null);
    setBusy(true);
    try {
      // Better Auth's magic-link plugin exposes the send endpoint at
      // `<basePath>/sign-in/magic-link`. `callbackURL` is where the emailed
      // link lands after verification — the dashboard (or the page the
      // AuthGate bounced the user from).
      const res = await fetch('/api/auth/sign-in/magic-link', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, callbackURL: next }),
      });
      if (!res.ok) {
        setError('Could not send link. Check the address and try again.');
        return;
      }
      setMagicSent(true);
    } finally {
      setBusy(false);
    }
  };

  const signInPassword = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await fetch('/api/auth/sign-in/email', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        setError('Invalid email or password.');
        return;
      }
      // The AuthProvider loaded `/me` once on mount (401 at that point).
      // Refresh it before navigating, otherwise the AuthGate still sees
      // `me === null` and bounces straight back to /login.
      await refresh();
      navigate(next, { replace: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-[100dvh] place-items-center bg-background px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 grid size-12 place-items-center rounded-2xl bg-primary text-primary-foreground elevate">
            <PawPrint className="size-5" strokeWidth={1.75} />
          </div>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Kennel Desk
          </p>
          <h1 className="font-heading text-3xl font-medium tracking-tight">Welcome back</h1>
        </div>

        <div className="rounded-2xl bg-surface ring-1 ring-border elevate p-6 md:p-8">
          {magicSent ? (
            <div className="space-y-3 text-center">
              <Mail className="mx-auto size-6 text-primary" strokeWidth={1.75} />
              <h2 className="font-heading text-base font-medium">Check your inbox</h2>
              <p className="text-sm text-muted-foreground">
                We've sent a sign-in link to{' '}
                <span className="font-medium text-foreground">{email}</span>.
              </p>
              <button
                type="button"
                onClick={() => setMagicSent(false)}
                className="text-sm text-primary underline underline-offset-4"
              >
                Use a different address
              </button>
            </div>
          ) : (
            <>
              <TabBar tab={tab} onChange={setTab} />
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (tab === 'magic') void sendMagic();
                  else void signInPassword();
                }}
              >
                <Field label="Email">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                  />
                </Field>
                {tab === 'password' && (
                  <Field label="Password">
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                    />
                  </Field>
                )}
                {error && <p className="text-sm text-destructive">{error}</p>}
                <button
                  type="submit"
                  disabled={busy || !email || (tab === 'password' && !password)}
                  className={cn(
                    'flex w-full items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground',
                    'px-4 py-2.5 text-sm font-medium press disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                >
                  {busy && <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />}
                  {tab === 'magic' ? 'Send sign-in link' : 'Sign in'}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Trouble signing in? Ask your account owner to re-send your invitation.
        </p>
      </div>
    </div>
  );
}

function TabBar({ tab, onChange }: { tab: Tab; onChange: (t: Tab) => void }) {
  return (
    <div className="mb-5 inline-flex w-full rounded-lg bg-secondary p-1 ring-1 ring-border">
      {(
        [
          { v: 'magic', label: 'Magic link', Icon: Mail },
          { v: 'password', label: 'Password', Icon: Lock },
        ] as const
      ).map((opt) => {
        const active = tab === opt.v;
        const { Icon } = opt;
        return (
          <button
            key={opt.v}
            type="button"
            onClick={() => onChange(opt.v)}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium press',
              active
                ? 'bg-surface text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="size-3.5" strokeWidth={1.75} />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: children always contains an input; biome can't introspect ReactNode
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
